/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Fragment, useMemo } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { ROLE } from '@/lib/roles'
import { useLayout } from '@/context/layout-provider'
import { useSidebarConfig } from '@/hooks/use-sidebar-config'
import { useSidebarData } from '@/hooks/use-sidebar-data'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
} from '@/components/ui/sidebar'
import { getNavGroupsForPath } from '../lib/workspace-registry'
import { NavGroup } from './nav-group'
import { NoyaProfileFooter } from './sidebar-user-popover'

/**
 * Noya application sidebar.
 *
 * Structure (matches nova_portal.html .sb):
 *   SidebarContent — nav groups with uppercase labels + teal active states (.sb-nav)
 *   SidebarFooter  — user chip that opens an upward popover (.sb-foot)
 *
 * Note: the top logo/brand header was intentionally removed — the top app bar
 * already renders SystemBrand, so a second copy at the sidebar top was redundant.
 */
export function AppSidebar() {
  const { t } = useTranslation()
  const { collapsible, variant } = useLayout()
  const { pathname } = useLocation()
  const userRole = useAuthStore((state) => state.auth.user?.role)
  const sidebarData = useSidebarData()

  // Nav group resolution — workspace-aware
  const allNavGroups = getNavGroupsForPath(pathname, t) || sidebarData.navGroups
  const configFilteredNavGroups = useSidebarConfig(allNavGroups)

  const currentNavGroups = useMemo(() => {
    const isAdmin = userRole && userRole >= ROLE.ADMIN
    return configFilteredNavGroups.filter((group) => {
      if (group.id === 'admin') return isAdmin
      return true
    })
  }, [configFilteredNavGroups, userRole])

  return (
    <Sidebar collapsible={collapsible} variant={variant}>
      {/* ── Nav groups (no top logo — the top app bar already shows the brand) ── */}
      <SidebarContent className='py-1'>
        {currentNavGroups.map((props, index) => {
          const key = props.id || props.title
          return (
            <Fragment key={key}>
              {/* Noya nav-divider: thin border between groups, matches nova_portal .nav-divider */}
              {index > 0 && (
                <hr
                  className='border-border mx-2 my-3 border-0 border-t'
                  aria-hidden='true'
                />
              )}
              <NavGroup {...props} />
            </Fragment>
          )
        })}
      </SidebarContent>

      {/* ── Footer: profile popup trigger ── */}
      <SidebarFooter className='border-border border-t p-2'>
        <NoyaProfileFooter />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
